/**
 * Non-blocking subscription CTA banner.
 * Shows on coach/learning pages when user has no active plan.
 * Does NOT block functionality -- just a prominent upgrade reminder.
 *
 * Features:
 * - Shows amber/purple gradient banner when no active subscription
 * - "View Plans" CTA navigates to /billing
 * - "Resume" button for paused subscriptions
 * - Trial countdown with upgrade CTA
 * - Dismissible for 4 hours (localStorage)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Crown, Pause, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { billingApi } from '@/services/billingApi';
import { useAuth } from '@/contexts/AuthContext';
import type { SubscriptionStatusResponse } from '@/types/billing';

const DISMISS_KEY = 'five_ai_sub_guard_dismissed';
const DISMISS_DURATION_MS = 4 * 60 * 60 * 1000; // 4 hours

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

function dismiss(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

/** Calculate days remaining until a date string (ISO 8601). */
function daysUntil(iso: string): number {
  const diff = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export interface SubscriptionGuardProps {
  className?: string;
}

export const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({ className }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [subStatus, setSubStatus] = useState<SubscriptionStatusResponse | null>(null);
  const [bannerType, setBannerType] = useState<'upgrade' | 'paused' | 'trial' | null>(null);

  useEffect(() => {
    if (!user?.id || isDismissed()) return;

    let cancelled = false;

    (async () => {
      try {
        const status = await billingApi.getSubscriptionStatus(user.id);
        if (cancelled) return;
        setSubStatus(status);

        const s = status.status;
        const isTrial = s === 'TRIAL' || status.isTrialActive === true;

        if (s === 'PAUSED') {
          setBannerType('paused');
          setVisible(true);
        } else if (isTrial && status.trialEndDate) {
          setBannerType('trial');
          setVisible(true);
        } else if (!s || s === 'INACTIVE' || s === 'EXPIRED' || s === 'CANCELLED') {
          setBannerType('upgrade');
          setVisible(true);
        }
        // ACTIVE / PENDING / PAST_DUE / SUSPENDED -- don't show banner
      } catch {
        // Fail silently -- never block user
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const handleDismiss = useCallback(() => {
    dismiss();
    setVisible(false);
  }, []);

  if (!visible || !bannerType) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className={className}
        >
          {/* Upgrade banner */}
          {bannerType === 'upgrade' && (
            <div className="rounded-xl border border-purple-500/30 bg-gradient-to-r from-amber-500/10 via-purple-500/10 to-fuchsia-500/10 dark:from-amber-500/15 dark:via-purple-500/15 dark:to-fuchsia-500/15 px-4 py-3 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500/20 to-purple-500/20 text-purple-600 dark:text-purple-400 mt-0.5">
                  <Crown className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-purple-800 dark:text-purple-200">
                    Upgrade to unlock unlimited AI coaching
                  </p>
                  <p className="text-xs text-purple-700/70 dark:text-purple-300/70 mt-0.5">
                    Get unlimited sessions, advanced analytics, and priority support.
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    className="h-7 rounded-lg text-xs bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white hover:from-purple-700 hover:to-fuchsia-700"
                    onClick={() => navigate('/billing')}
                  >
                    <Sparkles className="h-3 w-3 mr-1" />
                    View Plans
                  </Button>
                  <button
                    type="button"
                    onClick={handleDismiss}
                    className="flex h-6 w-6 items-center justify-center rounded-md text-purple-600/60 hover:text-purple-600 dark:text-purple-400/60 dark:hover:text-purple-400 hover:bg-purple-500/10 transition-colors"
                    aria-label="Dismiss"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Paused banner */}
          {bannerType === 'paused' && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 dark:bg-amber-500/10 px-4 py-3 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-600 dark:text-amber-400 mt-0.5">
                  <Pause className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    Your subscription is paused
                  </p>
                  <p className="text-xs text-amber-700/70 dark:text-amber-300/70 mt-0.5">
                    Resume anytime to continue your coaching journey.
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 rounded-lg text-xs border-amber-500/30 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10"
                    onClick={() => navigate('/billing')}
                  >
                    Resume
                  </Button>
                  <button
                    type="button"
                    onClick={handleDismiss}
                    className="flex h-6 w-6 items-center justify-center rounded-md text-amber-600/60 hover:text-amber-600 dark:text-amber-400/60 dark:hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
                    aria-label="Dismiss"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Trial banner */}
          {bannerType === 'trial' && subStatus?.trialEndDate && (
            <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 dark:bg-blue-500/10 px-4 py-3 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-500/15 text-blue-600 dark:text-blue-400 mt-0.5">
                  <Clock className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    {daysUntil(subStatus.trialEndDate)} day{daysUntil(subStatus.trialEndDate) !== 1 ? 's' : ''} left in your trial
                  </p>
                  <p className="text-xs text-blue-700/70 dark:text-blue-300/70 mt-0.5">
                    Upgrade now to keep all your progress and unlock unlimited features.
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    className="h-7 rounded-lg text-xs bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700"
                    onClick={() => navigate('/billing/plans')}
                  >
                    <Sparkles className="h-3 w-3 mr-1" />
                    Upgrade
                  </Button>
                  <button
                    type="button"
                    onClick={handleDismiss}
                    className="flex h-6 w-6 items-center justify-center rounded-md text-blue-600/60 hover:text-blue-600 dark:text-blue-400/60 dark:hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
                    aria-label="Dismiss"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
